/**
 * Defense-in-depth hardening for email HTML rendered via iframe `srcDoc`.
 *
 * Shared by every surface that injects stored email HTML into a frame —
 * `PixelPerfectEmailFrame` (canvas exact-import surface, template
 * detail), the templates gallery card fallback iframe, and the
 * analytics document preview. Client- and server-safe: no React, no DOM.
 */

const FRAME_CSP =
  "script-src 'none'; object-src 'none'; frame-src 'none'; child-src 'none'; " +
  "worker-src 'none'; connect-src 'none'; media-src 'none'; form-action 'none'; " +
  "base-uri 'none'"
const FRAME_CSP_META = `<meta http-equiv="Content-Security-Policy" content="${FRAME_CSP}">`
const META_TAG_PATTERN = /<meta\b(?:[^>"']|"[^"]*"|'[^']*')*>/gi
const HTTP_EQUIV_PATTERN =
  /\bhttp-equiv\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s"'=<>`]+))/i

function decodeAttributeEntities(value: string): string {
  const decodeCodePoint = (raw: string, radix: number) => {
    const codePoint = Number.parseInt(raw, radix)
    return Number.isFinite(codePoint) &&
      codePoint >= 0 &&
      codePoint <= 0x10_ff_ff
      ? String.fromCodePoint(codePoint)
      : '\ufffd'
  }
  return value
    .replace(/&#x([0-9a-f]+);?/gi, (_match, hex: string) =>
      decodeCodePoint(hex, 16)
    )
    .replace(/&#([0-9]+);?/g, (_match, decimal: string) =>
      decodeCodePoint(decimal, 10)
    )
    .replace(/&(Tab|NewLine);/gi, (match) =>
      match.toLowerCase() === '&tab;' ? '\t' : '\n'
    )
}

function isMetaRefreshTag(tag: string): boolean {
  const match = HTTP_EQUIV_PATTERN.exec(tag)
  const value = match?.[1] ?? match?.[2] ?? match?.[3]
  if (!value) {
    return false
  }
  return (
    decodeAttributeEntities(value)
      .replace(/[\s\u0000-\u001f]+/g, '')
      .toLowerCase() === 'refresh'
  )
}

/**
 * The frame is a light surface, whatever the viewer's browser prefers. An
 * email's `@media (prefers-color-scheme: dark)` block was written for a
 * dark inbox — Loops, Customer.io and MJML output force white copy in it —
 * and in a dark-mode browser it would paint white text onto the email's
 * own white background (a "blank" email on the flow page). The condition
 * is rewritten so it never matches, and a `color-scheme` that admits dark
 * is pinned to light so the canvas never goes dark under a transparent
 * body. The email then renders as authored for light, which is what its
 * screenshot shows.
 */
const DARK_SCHEME_CONDITION = /\(\s*prefers-color-scheme\s*:\s*dark\s*\)/gi
const NEVER_MATCHING_CONDITION = '(min-width: 1px) and (max-width: 0px)'
const COLOR_SCHEME_DECLARATION =
  /color-scheme\s*:\s*([^;"'}]*\bdark\b[^;"'}]*)/gi
const COLOR_SCHEME_META =
  /(<meta\b[^>]*\bname\s*=\s*["']color-scheme["'][^>]*\bcontent\s*=\s*["'])([^"']*)(["'])/gi

export function pinEmailFrameToLightScheme(html: string): string {
  return html
    .replace(DARK_SCHEME_CONDITION, NEVER_MATCHING_CONDITION)
    .replace(COLOR_SCHEME_DECLARATION, 'color-scheme: light')
    .replace(COLOR_SCHEME_META, '$1light$3')
}

/**
 * Add a defense-in-depth CSP without reparsing or serializing the document.
 * String insertion is intentional: DOM serialization can normalize source
 * whitespace/attributes and change email pixels. A refresh directive is the
 * only script-free primitive that can navigate the frame away from srcDoc.
 */
export function hardenEmailFrameHtml(html: string): string {
  const withoutRefresh = pinEmailFrameToLightScheme(html).replace(
    META_TAG_PATTERN,
    (tag) => (isMetaRefreshTag(tag) ? '' : tag)
  )
  if (/<head\b[^>]*>/i.test(withoutRefresh)) {
    return withoutRefresh.replace(
      /<head\b[^>]*>/i,
      (opening) => `${opening}${FRAME_CSP_META}`
    )
  }
  if (/<html\b[^>]*>/i.test(withoutRefresh)) {
    return withoutRefresh.replace(
      /<html\b[^>]*>/i,
      (opening) => `${opening}<head>${FRAME_CSP_META}</head>`
    )
  }
  return `${FRAME_CSP_META}${withoutRefresh}`
}
